import { ApiClient } from './apiClient'
import type { User } from '@/types/user'
import type { Organization } from '@/types/organization'
import type { Team } from '@/types/team'

class UserService extends ApiClient {
  async fetchAll(): Promise<User[]> {
    const response = await this.get<User[]>('/users')
    return response.data
  }

  async fetchById(userId: string): Promise<User> {
    const response = await this.get<any>(`/users/${userId}`)
    const u = response.data
    // Tolerate both casings: the Go User struct uses `json:"firstName"`
    // for the name fields while every other column is snake_case. Reading
    // only one casing made freshly-fetched users come back with
    // `firstName: undefined`, which broke every flow that re-fetched after
    // a name edit (the roster's Name column fell back to its placeholder).
    return {
      id: u.id,
      email: u.email,
      firstName: u.first_name ?? u.firstName ?? null,
      lastName: u.last_name ?? u.lastName ?? null,
      language: u.language,
      createdAt: u.created_at ?? u.createdAt,
      updatedAt: u.updated_at ?? u.updatedAt,
      roles: [],
    }
  }

  async fetchByIDs(userIds: string[]): Promise<User[]> {
    if (!userIds || userIds.length === 0) return []
    const response = await this.get<User[]>('/users', {
      params: { ids: userIds.join(',') },
    })
    return response.data
  }

  async fetchCurrent(): Promise<User> {
    const response = await this.get<User>('/users/me')
    return response.data
  }

  async fetchCurrentOrganization(): Promise<Organization> {
    const response = await this.get<Organization>('/users/me/organization')
    return response.data
  }

  async fetchTeams(userId: string): Promise<Team[]> {
    const response = await this.get<Team[]>(`/users/${userId}/teams`)
    return response.data
  }

  async updateTeamUserRole(teamId: string, userId: string, newRole: string): Promise<void> {
    await this.patch(`/teams/${teamId}/users/${userId}`, { role: newRole })
  }

  async deleteTeamUser(teamId: string, userId: string): Promise<void> {
    await this.delete(`/teams/${teamId}/users/${userId}`)
  }

  async updateInfo(user: User): Promise<void> {
    await this.patch(`/users/${user.id}/info`, {
      first_name: user.firstName,
      last_name: user.lastName,
      email: user.email,
      language: user.language,
      id: user.id,
    })
  }
  
  async update(user: User): Promise<void> {
    await this.put(
      `/users/${user.id}`,
      toUserWirePayload(user),
      { version: 'v2' },
    )
  }
  // v2: soft-delete the user and hard-delete their user_roles +
  // context_user_roles. Returns void on 204.
  async deleteUser(userId: string): Promise<void> {
    await this.delete(`/users/${userId}`, { version: 'v2' })
  }
}

export const userService = new UserService()

function toUserWirePayload(user: User): Record<string, unknown> {
  return {
    id: user.id,
    email: user.email,
    first_name: user.firstName ?? null,
    last_name: user.lastName ?? null,
    language: user.language ?? null,
    // organization_id is no longer a column on the user (membership lives in
    // user_entities) and the v2 update endpoint ignored it, so it's not sent.
    created_at: user.createdAt ?? null,
    updated_at: user.updatedAt ?? null,
  }
}
